import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

// Reusable accessible modal dialog, styled by the existing `.modal-*` classes (App.css).
//
// - Rendered into document.body through a portal, so no ancestor's overflow / z-index can clip it.
// - role="dialog" + aria-modal, named by its title (aria-labelledby).
// - Focus moves into the dialog when it opens, Tab / Shift+Tab cycle inside it, and focus returns to
//   whatever opened it when it closes. Page scroll is locked while it is open.
// - The header and footer stay put and only the body scrolls, so Save / Cancel are always in reach of a
//   long form (`.modal-content--pinned-chrome` in App.css).
// - Escape and the × button always close it; clicking the backdrop closes it unless `closeOnBackdrop`
//   is false (e.g. while a form holds unsaved input, so a stray click can't throw the work away).

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])', 'select:not([disabled])',
  'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  /** Rendered in the `.modal-footer` bar (typically Cancel + the primary action). */
  footer?: React.ReactNode;
  /** Close on a click on the dimmed backdrop. Default true. */
  closeOnBackdrop?: boolean;
  /** Override the dialog's max width (the CSS default is 640px). */
  maxWidth?: string;
}

const Modal: React.FC<ModalProps> = ({ open, onClose, title, children, footer, closeOnBackdrop = true, maxWidth }) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  // Latest onClose without re-running the open/close effect whenever the parent re-renders.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus the first control in the body (not the × button), falling back to the dialog itself.
    const dialog = dialogRef.current;
    const focusables = dialog ? Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)) : [];
    const firstField = focusables.find(el => !el.classList.contains('modal-close')) ?? focusables[0];
    (firstField ?? dialog)?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function' && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCloseRef.current();
      return;
    }
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusables = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusables.length === 0) { e.preventDefault(); return; }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === dialogRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="modal-overlay"
      data-testid="modal-backdrop"
      onMouseDown={(e) => {
        // Only a press that starts AND lands on the backdrop itself — not a text selection dragged out.
        if (e.target === e.currentTarget && closeOnBackdrop) onCloseRef.current();
      }}
    >
      <div
        ref={dialogRef}
        className="modal-content modal-content--pinned-chrome"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        style={maxWidth ? { maxWidth } : undefined}
      >
        <div className="modal-header">
          <h3 id={titleId}>{title}</h3>
          <button type="button" className="modal-close" onClick={() => onCloseRef.current()} aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
